import React from "react";
import { ArrowRight } from "lucide-react";
import { Button } from "../components/ui/button";
import Reveal from "../components/Reveal";
import AnimatedCounter from "../components/AnimatedCounter";
import { PageHero } from "./Services";
import { values, team, stats, company, hero } from "../data/mock";
import Seo from "../components/Seo";

const About = () => {
  return (
    <div className="overflow-hidden">
      <Seo title="About Us — Our Story & Team" description="Jellis Enterprise is a top digital marketing agency offering every service under one roof, backed by 25+ years of combined experience." />
      <PageHero
        eyebrow="About Us"
        title="A top marketing agency built on results"
        subtitle="We saw businesses struggling to stitch together multiple agencies — so we built one team that does it all, and does it better."
      />

      {/* Story */}
      <section className="section-pad bg-white">
        <div className="mx-auto max-w-7xl px-6 grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <Reveal className="relative">
            <div className="rounded-3xl overflow-hidden shadow-xl ring-1 ring-slate-200">
              <img
                src="https://images.unsplash.com/photo-1519389950473-47ba0277781c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTV8MHwxfHNlYXJjaHwxfHxkaWdpdGFsJTIwbWFya2V0aW5nJTIwdGVhbXxlbnwwfHx8fDE3ODc2MDU5NTd8MA&ixlib=rb-4.1.0&q=85"
                alt="Jellis Enterprise team at work"
                className="w-full h-[460px] object-cover"
              />
            </div>
            <div className="absolute -bottom-6 -right-6 glass rounded-2xl shadow-xl border border-white/60 px-6 py-5 animate-float">
              <p className="font-display text-3xl font-extrabold text-blue-600">25+</p>
              <p className="text-sm font-semibold text-slate-600">Years combined experience</p>
            </div>
          </Reveal>
          <Reveal delay={120}>
            <p className="text-sm font-bold uppercase tracking-[0.2em] text-blue-600">Our story</p>
            <h2 className="mt-3 font-display text-3xl md:text-4xl font-extrabold text-slate-900">
              Every digital service, one dedicated team
            </h2>
            <div className="mt-5 space-y-4 text-lg text-slate-600 leading-relaxed">
              <p>
                Jellis Enterprise offers US-based clients a complete platform of digital marketing services designed for businesses of every size and structure.
              </p>
              <p>
                We saw that too many businesses were forced to chase down multiple agencies to cover their needs — and still weren’t getting results. We listened, and set out to do something different.
              </p>
              <p>
                Today we’re proud to offer every type of digital advertising service and near-instant solutions to the hurdles slowing your growth online.
              </p>
            </div>
            <a href={company.bookingUrl} target="_blank" rel="noreferrer">
              <Button size="lg" className="mt-8 brand-gradient text-white font-bold group">
                Work with us
                <ArrowRight className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-1" />
              </Button>
            </a>
          </Reveal>
        </div>
      </section>

      {/* Stats band */}
      <section className="relative section-pad navy-gradient overflow-hidden">
        <div className="blob h-72 w-72 bg-blue-500 top-0 left-1/4" />
        <div className="relative mx-auto max-w-7xl px-6 grid grid-cols-2 lg:grid-cols-4 gap-8">
          {stats.map((s, i) => (
            <Reveal key={i} delay={i * 80} className="text-center">
              <AnimatedCounter end={s.value} suffix={s.suffix} className="font-display text-5xl md:text-6xl font-extrabold text-white" />
              <p className="mt-3 text-sm font-semibold uppercase tracking-wide text-blue-200/80">{s.label}</p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Values */}
      <section className="section-pad bg-white">
        <div className="mx-auto max-w-7xl px-6">
          <Reveal className="text-center max-w-2xl mx-auto">
            <p className="text-sm font-bold uppercase tracking-[0.2em] text-blue-600">What drives us</p>
            <h2 className="mt-3 font-display text-3xl md:text-4xl font-extrabold text-slate-900">Our core values</h2>
          </Reveal>
          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((v, i) => (
              <Reveal key={v.title} delay={i * 80}>
                <div className="h-full rounded-2xl border border-slate-200 p-7 card-lift bg-gradient-to-b from-white to-slate-50/50">
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl brand-gradient shadow-lg">
                    <v.icon className="h-6 w-6 text-white" />
                  </div>
                  <h3 className="mt-5 font-display text-lg font-bold text-slate-900">{v.title}</h3>
                  <p className="mt-2 text-sm text-slate-600 leading-relaxed">{v.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Team */}
      <section className="section-pad bg-slate-50">
        <div className="mx-auto max-w-7xl px-6">
          <Reveal className="text-center max-w-2xl mx-auto">
            <p className="text-sm font-bold uppercase tracking-[0.2em] text-blue-600">The people</p>
            <h2 className="mt-3 font-display text-3xl md:text-4xl font-extrabold text-slate-900">Meet the leadership</h2>
          </Reveal>
          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {team.map((m, i) => (
              <Reveal key={m.name} delay={i * 80}>
                <div className="rounded-2xl border border-slate-200 bg-white p-8 text-center card-lift">
                  <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full brand-gradient text-white font-display text-2xl font-extrabold shadow-lg">
                    {m.initials}
                  </div>
                  <h3 className="mt-5 font-display text-lg font-bold text-slate-900">{m.name}</h3>
                  <p className="mt-1 text-sm font-semibold text-blue-600">{m.role}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
